/**
 * Support Guide: Set Up the OBS Overlay
 * 550 frames @ 30fps = ~18 seconds
 *
 * Timeline:
 *  0-45      Title card
 *  45-165    Competition view — copy OBS URL (matches real CompetitionView)
 *  165-420   OBS Studio — right-click scene, add Browser, set dims, paste URL, OK
 *  420-550   OBS with overlay visible in the preview area (not fullscreen)
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { TypeWriter } from "../../components/TypeWriter";
import { AutoCursor } from "../../components/AutoCursor";
import { ScreenMock } from "../../components/ScreenMock";
import { StepLabel } from "../../components/StepLabel";

const ACCENT = "#22c55e";
const MONO = "'JetBrains Mono', monospace";

export function SetupOBSGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={45}><TitleCard /></Sequence>
      <Sequence from={45} durationInFrames={120}><CompViewScene /></Sequence>
      <Sequence from={165} durationInFrames={255}><OBSScene /></Sequence>
      <Sequence from={420} durationInFrames={130}><OBSWithOverlay /></Sequence>
    </AbsoluteFill>
  );
}

function TitleCard() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 20], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: MONO, marginBottom: 16 }}>Producer Training</div>
        <div style={{ fontSize: 52, fontWeight: 800, color: "#fff", maxWidth: 800, lineHeight: 1.1 }}>Set Up the OBS Overlay</div>
        <div style={{ marginTop: 20 }}><TypeWriter text="Copy URL → Add Browser Source → Go Live" delay={10} speed={1} style={{ fontSize: 16, color: "#94a3b8" }} /></div>
      </div>
    </AbsoluteFill>
  );
}

/* ── Scene 1: CompetitionView — copy the OBS URL ── */

function CompViewScene() {
  const frame = useCurrentFrame();
  const copied = frame >= 60;

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/boardstock">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: "#e4e4e7" }}>COMPETITIONS</span>
            <span style={navLink}>RIDERS</span>
            <span style={navLink}>TRAINING</span>
            <span style={navLink}>SUPPORT</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#71717a" }}>Takoda Mundy</span>
          </div>
          <div style={{ padding: "24px 32px" }}>
            {/* Header — matches real CompetitionView */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 24, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>BOARDSTOCK</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                <span style={{ fontSize: 14, color: "#71717a" }}>Competition</span>
                <span style={{ ...statusBadge, background: "#ef4444", color: "#fff" }}>LIVE</span>
              </div>
              <div style={{ marginTop: 6 }}>
                <span style={{ ...roleBadge, background: "#3b82f6" }}>Admin</span>
              </div>
            </div>

            {/* 3-col cards: Setup, Judge, Producer */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
              <div style={cardStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Setup</div>
                <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Configure divisions, heats, assign riders, and invite your team.</div>
                <div style={{ marginTop: 10 }}><span style={{ ...roleBadge, background: "#3b82f6" }}>Admin</span></div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Judge</div>
                <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Score riders in real-time with 0.1 precision.</div>
                <div style={{ marginTop: 10 }}><span style={{ ...roleBadge, background: "#22c55e" }}>Access</span></div>
              </div>
              <div style={cardStyle}>
                <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>Producer</div>
                <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Control the live overlay. Focus riders, show scores, leaderboard.</div>
                <div style={{ marginTop: 10 }}><span style={{ ...roleBadge, background: "#a855f7" }}>Access</span></div>
              </div>
            </div>

            {/* OBS Overlay card — separate, below grid */}
            <div style={{ ...cardStyle, maxWidth: 480 }}>
              <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" }}>OBS Overlay</div>
              <div style={{ fontSize: 12, color: "#71717a", marginTop: 6 }}>Add this URL as a Browser Source in OBS (1920x1080). No login required.</div>
              <div style={{ marginTop: 12 }}>
                <code id="obs-url-field" style={{ display: "block", padding: "10px 12px", background: "#0a0a0b", border: "1px solid #2a2a32", fontFamily: MONO, fontSize: 11, color: "#a1a1aa", wordBreak: "break-all" as const }}>
                  https://raley.koda-software.com/obs/262b6dda1e344cad9...
                </code>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <div style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", background: "#1a1a1e", color: "#e4e4e7", border: "1px solid #2a2a32" }}>Open</div>
                  <div id="obs-copy-btn" style={{ padding: "4px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: copied ? "#22c55e" : "#71717a", cursor: "pointer" }}>
                    {copied ? "Copied!" : "Copy URL"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <AutoCursor points={[
          { x: 960, y: 400, frame: 0 },
          { target: "obs-url-field", frame: 20 },
          { target: "obs-url-field", frame: 35 },
          { target: "obs-copy-btn", frame: 45 },
          { target: "obs-copy-btn", frame: 60, click: true },
          { x: 960, y: 400, frame: 110 },
        ]} />
      </ScreenMock>
      <StepLabel text="Copy the OBS overlay URL from your competition page" show={5} hide={110} color={ACCENT} />
    </AbsoluteFill>
  );
}

/* ── Scene 2: OBS Studio — right-click, add browser source, configure ── */

function OBSScene() {
  const frame = useCurrentFrame();

  // Timeline: right-click (40) → menu appears → click Browser (80) → props dialog (90) → hover width (120) → hover height (140) → click URL (160) → type URL → click OK (230)
  const showContextMenu = frame >= 40 && frame < 80;
  const browserAdded = frame >= 80;
  const showProps = frame >= 90;
  const dimsHighlight = frame >= 115 && frame < 155;
  const urlTyping = frame > 160 ? Math.min(Math.floor((frame - 160) / 1.5), 48) : 0;
  const url = "raley.koda-software.com/obs/262b6dda1e344cad9...".slice(0, urlTyping);
  const urlHighlight = frame >= 155;

  const stepText =
    frame < 40 ? "Right-click the preview to add a source" :
    frame < 80 ? "Select Browser from the menu" :
    frame < 155 ? "Set Width to 1920 and Height to 1080" :
    frame < 220 ? "Paste the overlay URL you copied" :
    "Click OK to add the source";

  return (
    <AbsoluteFill>
      <div style={{ width: 1920, height: 1080, position: "relative" }}>
        <OBSMock showContextMenu={showContextMenu} browserAdded={browserAdded} />
        {showProps && (
          <div style={{
            position: "absolute", top: 120, left: 400, width: 700,
            background: "#1e1e2e", border: "1px solid #3a3a4e", zIndex: 50,
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            opacity: interpolate(frame, [90, 100], [0, 1], { extrapolateRight: "clamp" }),
          }}>
            <div style={{ height: 32, background: "#181825", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", padding: "0 14px" }}>
              <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>Properties — Browser Source</span>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>URL</div>
                <div id="obs-url-input" style={{ padding: "10px 12px", background: "#11111b", border: `1px solid ${urlHighlight ? ACCENT : "#2a2a32"}`, fontSize: 13, color: url ? "#e4e4e7" : "#52525b", fontFamily: MONO }}>
                  {url || "https://"}{urlHighlight && <span style={{ color: ACCENT }}>|</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 20, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>Width</div>
                  <div id="obs-width-field" style={{ padding: "10px 12px", background: "#11111b", border: `1px solid ${dimsHighlight ? ACCENT : "#2a2a32"}`, fontSize: 16, fontWeight: 700, color: dimsHighlight ? "#fff" : "#e4e4e7", fontFamily: MONO }}>1920</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>Height</div>
                  <div id="obs-height-field" style={{ padding: "10px 12px", background: "#11111b", border: `1px solid ${dimsHighlight ? ACCENT : "#2a2a32"}`, fontSize: 16, fontWeight: 700, color: dimsHighlight ? "#fff" : "#e4e4e7", fontFamily: MONO }}>1080</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <div id="obs-ok-btn" style={{ padding: "8px 24px", background: "#3b82f6", fontSize: 12, color: "#fff", fontWeight: 600 }}>OK</div>
                <div style={{ padding: "8px 24px", background: "#2a2a32", fontSize: 12, color: "#71717a" }}>Cancel</div>
              </div>
            </div>
          </div>
        )}
        {/* Cursor — SLOW movements */}
        <AutoCursor points={[
          { x: 700, y: 400, frame: 0 },
          // Right-click in the preview area
          { x: 700, y: 400, frame: 30 },
          { x: 700, y: 400, frame: 40, click: true },
          // Slowly move to Browser in context menu
          { target: "ctx-browser", frame: 60 },
          { target: "ctx-browser", frame: 80, click: true },
          // Hover over width
          { target: "obs-width-field", frame: 115 },
          { target: "obs-width-field", frame: 130 },
          // Hover over height
          { target: "obs-height-field", frame: 135 },
          { target: "obs-height-field", frame: 150 },
          // Click URL field
          { target: "obs-url-input", frame: 160, click: true },
          // Wait while typing
          { target: "obs-url-input", frame: 215 },
          // Slowly move to OK
          { target: "obs-ok-btn", frame: 225 },
          { target: "obs-ok-btn", frame: 240, click: true },
          { x: 700, y: 400, frame: 250 },
        ]} />
      </div>
      <StepLabel text={stepText} show={0} hide={250} color={ACCENT} />
    </AbsoluteFill>
  );
}

/* ── Scene 3: OBS with overlay visible INSIDE the preview area ── */

function OBSWithOverlay() {
  const frame = useCurrentFrame();
  const overlayOp = interpolate(frame, [10, 40], [0, 1], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill>
      <div style={{ width: 1920, height: 1080, position: "relative" }}>
        <OBSMock showContextMenu={false} browserAdded={true} overlayVisible={true} overlayOpacity={overlayOp} />
      </div>
      <StepLabel text="Your overlay is now live inside OBS — control it from the Producer screen" show={10} hide={120} color={ACCENT} />
    </AbsoluteFill>
  );
}

/* ── OBS Studio Mock ── */

function OBSMock({ showContextMenu, browserAdded, overlayVisible, overlayOpacity }: { showContextMenu: boolean; browserAdded: boolean; overlayVisible?: boolean; overlayOpacity?: number }) {
  return (
    <div style={{ width: 1920, height: 1080, background: "#1e1e2e", display: "flex", flexDirection: "column", fontFamily: "'Inter', sans-serif" }}>
      {/* Title bar */}
      <div style={{ height: 36, background: "#181825", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", padding: "0 14px", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#ef4444" }} />
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#eab308" }} />
          <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#22c55e" }} />
        </div>
        <span style={{ fontSize: 13, color: "#71717a", fontWeight: 600 }}>OBS Studio 30.2</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 16 }}>
          {["File", "Edit", "View", "Docks", "Tools", "Help"].map((m) => (
            <span key={m} style={{ fontSize: 11, color: "#94a3b8" }}>{m}</span>
          ))}
        </div>
      </div>

      {/* Main preview area */}
      <div style={{ flex: 1, background: "#11111b", display: "flex", justifyContent: "center", alignItems: "center", padding: 32, position: "relative" }}>
        <div style={{ width: "85%", height: "80%", background: "#000", border: "1px solid #2a2a32", position: "relative", overflow: "hidden" }}>
          {/* Camera feed placeholder */}
          <div style={{ width: "100%", height: "100%", background: "linear-gradient(180deg, #1a3050 0%, #0a1828 60%, #061220 100%)", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <span style={{ fontSize: 16, color: "#2a2a32", fontWeight: 600 }}>Main Scene — Camera Feed</span>
          </div>
          {/* Overlay visible inside preview */}
          {overlayVisible && (
            <div style={{ position: "absolute", inset: 0, opacity: overlayOpacity ?? 1 }}>
              {/* Mini overlay elements */}
              <div style={{ position: "absolute", top: 12, left: 16, display: "flex", alignItems: "center", gap: 6, background: "rgba(10,10,11,0.88)", padding: "4px 12px" }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 4px #ef4444" }} />
                <span style={{ fontSize: 10, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>Men's — Semi-Finals</span>
              </div>
              <div style={{ position: "absolute", bottom: 16, left: 16, right: 16, display: "flex", gap: 6 }}>
                {[{ init: "HC", c: "#3b82f6", s: "8.7" }, { init: "NR", c: "#22c55e", s: "7.2" }, { init: "DF", c: "#eab308", s: "-" }, { init: "AW", c: "#ef4444", s: "9.0" }].map((r) => (
                  <div key={r.init} style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(10,10,11,0.88)", padding: "4px 8px" }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: r.c, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 700, color: "#fff" }}>{r.init}</div>
                    <span style={{ fontSize: 9, color: "#94a3b8", fontFamily: MONO }}>{r.s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Right-click context menu */}
        {showContextMenu && (
          <div style={{ position: "absolute", top: 300, left: 620, background: "#1a1a2e", border: "1px solid #3a3a4e", padding: "4px 0", zIndex: 60, minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
            {["Add", "—", "Copy", "Paste", "—", "Transform", "Order", "—", "Filters", "Properties"].map((item, i) => (
              item === "—" ? <div key={i} style={{ height: 1, background: "#2a2a32", margin: "2px 0" }} /> :
              item === "Add" ? (
                <div key={item} style={{ padding: "6px 16px", fontSize: 12, color: "#fff", background: "#3b82f6", fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
                  <span>Add</span><span style={{ fontSize: 10 }}>▶</span>
                </div>
              ) : (
                <div key={item} style={{ padding: "6px 16px", fontSize: 12, color: "#94a3b8" }}>{item}</div>
              )
            ))}
            {/* Sub-menu for Add */}
            <div style={{ position: "absolute", top: 0, left: "100%", background: "#1a1a2e", border: "1px solid #3a3a4e", padding: "4px 0", minWidth: 180, boxShadow: "0 8px 24px rgba(0,0,0,0.5)" }}>
              {["Image", "Image Slide Show", "Scene", "Media Source", "Browser", "Color Source", "Display Capture", "Window Capture", "Video Capture"].map((item) => (
                <div key={item} id={item === "Browser" ? "ctx-browser" : undefined} style={{
                  padding: "6px 16px", fontSize: 12,
                  color: item === "Browser" ? "#fff" : "#94a3b8",
                  background: item === "Browser" ? "#3b82f6" : "transparent",
                  fontWeight: item === "Browser" ? 700 : 400,
                }}>{item}</div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bottom panels */}
      <div style={{ height: 180, display: "flex", borderTop: "1px solid #2a2a32" }}>
        <div style={{ width: 260, padding: 10, borderRight: "1px solid #2a2a32" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 }}>Scenes</div>
          <div style={{ padding: "5px 8px", background: "#2a2a32", fontSize: 11, color: "#e4e4e7", marginBottom: 3 }}>Main Scene</div>
          <div style={{ padding: "5px 8px", fontSize: 11, color: "#71717a" }}>BRB Scene</div>
        </div>
        <div style={{ width: 300, padding: 10, borderRight: "1px solid #2a2a32" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 }}>Sources</div>
          {browserAdded && <div style={{ padding: "5px 8px", background: ACCENT, fontSize: 11, color: "#fff", fontWeight: 600, marginBottom: 3 }}>Browser — Raley Overlay</div>}
          <div style={{ padding: "5px 8px", background: "#181825", fontSize: 11, color: "#e4e4e7", marginBottom: 3 }}>Video Capture (Camera)</div>
          <div style={{ padding: "5px 8px", fontSize: 11, color: "#71717a" }}>Audio Input Capture</div>
        </div>
        <div style={{ flex: 1, padding: 10, borderRight: "1px solid #2a2a32" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 }}>Audio Mixer</div>
          {["Desktop Audio", "Mic/Aux"].map((a) => (
            <div key={a} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 9, color: "#71717a", width: 80 }}>{a}</span>
              <div style={{ flex: 1, height: 5, background: "#11111b" }}><div style={{ width: "55%", height: "100%", background: ACCENT }} /></div>
            </div>
          ))}
        </div>
        <div style={{ width: 180, padding: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", marginBottom: 6 }}>Controls</div>
          {["Start Streaming", "Start Recording", "Settings", "Exit"].map((c) => (
            <div key={c} style={{ padding: "4px 10px", background: "#2a2a32", fontSize: 10, color: "#e4e4e7", textAlign: "center" as const, marginBottom: 2 }}>{c}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Styles ── */
const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: MONO, fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a" };
const cardStyle: React.CSSProperties = { background: "#111113", border: "1px solid #2a2a32", padding: 16 };
const statusBadge: React.CSSProperties = { display: "inline-flex", padding: "2px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: MONO };
const roleBadge: React.CSSProperties = { display: "inline-flex", padding: "2px 8px", fontSize: 10, fontWeight: 600, textTransform: "capitalize", color: "#fff" };
